/**
 * MobileRiskSection Component
 *
 * Mobile-optimized risk profile management for the Risk Profiles section
 * of MobileAccountDetail. Provides:
 * - Profile selector (Select dropdown with default-star indicator)
 * - Profile management actions (create, rename, archive, set-as-default)
 * - Profile description editing
 * - Account default risk input with %/$ toggle
 *
 * Assumes parent has already guarded for loading/null accountRisk state.
 * Integrates MobileCascadeEditor for strategy/playbook override management.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Star, Trash2 } from 'lucide-react';

// 2. Workspace packages
import {
  Button,
  ButtonGroup,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectItem,
  TextArea,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AccountRisk, RiskProfile, RiskSpec } from '@/types/schema/AccountRisk';

// 4. Relative imports
import { useAccounts } from '../context';
import { MobileCascadeEditor } from './MobileCascadeEditor';
import { resolveProfileId, toDisplayValue, toStoredValue, defaultValueForType } from './riskHelpers';

// =============================================================================
// Component
// =============================================================================

export const MobileRiskSection: React.FC = () => {
  const {
    accountRisk,
    setAccountRisk,
    formState,
    activeBrokers,
    strategies,
    strategyPlaybooks,
  } = useAccounts();

  // ---------------------------------------------------------------------------
  // Local UI state
  // ---------------------------------------------------------------------------
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [profileModalMode, setProfileModalMode] = useState<'create' | 'rename' | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmingDefaultId, setConfirmingDefaultId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const activeProfiles = useMemo(
    () => (accountRisk ? accountRisk.riskProfiles.filter(p => p.status === 'active') : []),
    [accountRisk],
  );

  const selectedProfile = selectedProfileId
    ? activeProfiles.find(p => p.id === selectedProfileId) ?? null
    : null;

  const defaultProfileId = accountRisk?.defaultProfileId ?? null;

  // Currency resolution: account override → broker default → 'USD'
  const brokerCurrency = activeBrokers
    .find(b => b.id === formState.brokerId)
    ?.feeMetadata?.defaultAccountCurrency;
  const currency = formState.baseCurrency || brokerCurrency || 'USD';

  // ---------------------------------------------------------------------------
  // Profile revalidation effect
  // Covers: entity change (full reset), profile archive/sync, default change
  // ---------------------------------------------------------------------------
  const accountRiskId = accountRisk?.id;
  const activeProfileIds = activeProfiles.map(p => p.id).join(',');
  const currentDefaultId = accountRisk?.defaultProfileId;

  useEffect(() => {
    if (!selectedProfileId || !activeProfiles.some(p => p.id === selectedProfileId)) {
      setSelectedProfileId(resolveProfileId(accountRisk));
    }
    // Intentionally uses derived primitives, not full accountRisk object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountRiskId, activeProfileIds, currentDefaultId]);

  // ---------------------------------------------------------------------------
  // Uniqueness validation
  // ---------------------------------------------------------------------------
  const isNameDuplicate = useCallback((name: string, excludeProfileId?: string): boolean => {
    if (!accountRisk) return false;
    const normalized = name.trim().toLowerCase();
    if (!normalized) return false;
    return accountRisk.riskProfiles.some(
      p => p.status === 'active' && p.name.toLowerCase() === normalized
        && (!excludeProfileId || p.id !== excludeProfileId),
    );
  }, [accountRisk]);

  // ---------------------------------------------------------------------------
  // canDeleteProfile guard
  // ---------------------------------------------------------------------------
  const canDeleteProfile = useCallback((profileId: string): boolean => {
    if (!accountRisk) return false;
    const active = accountRisk.riskProfiles.filter(p => p.status === 'active');
    if (active.length <= 1) return false;
    if (accountRisk.defaultProfileId === profileId) return false;
    return true;
  }, [accountRisk]);

  // ---------------------------------------------------------------------------
  // Mutation handlers
  // ---------------------------------------------------------------------------

  const handleAddProfile = useCallback((name: string): void => {
    if (!accountRisk) return;
    const currentActive = accountRisk.riskProfiles.filter(p => p.status === 'active');
    const newId = crypto.randomUUID();
    const newProfile: RiskProfile = {
      id: newId,
      name,
      status: 'active',
      order: currentActive.length,
      accountDefault: { type: 'percentage', value: 0.01 },
    };
    setAccountRisk({
      ...accountRisk,
      riskProfiles: [...accountRisk.riskProfiles, newProfile],
    });
    setSelectedProfileId(newId);
  }, [accountRisk, setAccountRisk]);

  const handleDeleteProfile = useCallback((profileId: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? { ...p, status: 'archived' as const } : p
      ),
    });
    if (selectedProfileId === profileId) {
      const remaining = accountRisk.riskProfiles.filter(
        p => p.id !== profileId && p.status === 'active'
      );
      setSelectedProfileId(remaining[0]?.id ?? null);
    }
  }, [accountRisk, setAccountRisk, selectedProfileId]);

  const handleRenameProfile = useCallback((profileId: string, newName: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? { ...p, name: newName } : p
      ),
    });
  }, [accountRisk, setAccountRisk]);

  const handleSetDefault = useCallback((profileId: string | null): void => {
    if (!accountRisk) return;
    if (profileId) {
      setAccountRisk({ ...accountRisk, defaultProfileId: profileId });
    } else {
      // Remove defaultProfileId — cannot set to undefined with exactOptionalPropertyTypes
      const { defaultProfileId: _, ...rest } = accountRisk;
      setAccountRisk(rest as AccountRisk);
    }
  }, [accountRisk, setAccountRisk]);

  const handleUpdateDescription = useCallback((profileId: string, description: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p => {
        if (p.id !== profileId) return p;
        if (description) return { ...p, description };
        // Remove description field when empty (exactOptionalPropertyTypes)
        const { description: _, ...withoutDesc } = p;
        return withoutDesc as RiskProfile;
      }),
    });
  }, [accountRisk, setAccountRisk]);

  const handleUpdateProfile = useCallback((profileId: string, updatedProfile: RiskProfile): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? updatedProfile : p
      ),
    });
  }, [accountRisk, setAccountRisk]);

  // ---------------------------------------------------------------------------
  // Account Default Risk handlers
  // ---------------------------------------------------------------------------
  const handleAccountDefaultInputChange = useCallback((rawValue: string): void => {
    if (!accountRisk || !selectedProfileId) return;
    const selectedProf = accountRisk.riskProfiles.find(p => p.id === selectedProfileId);
    if (!selectedProf) return;
    const inputValue = rawValue === '' ? 0 : parseFloat(rawValue);
    if (isNaN(inputValue)) return;
    const updatedRisk: RiskSpec = {
      ...selectedProf.accountDefault,
      value: toStoredValue(inputValue, selectedProf.accountDefault.type),
    };
    handleUpdateProfile(selectedProfileId, { ...selectedProf, accountDefault: updatedRisk });
  }, [accountRisk, selectedProfileId, handleUpdateProfile]);

  const handleAccountDefaultTypeToggle = useCallback((isFixed: boolean): void => {
    if (!accountRisk || !selectedProfileId) return;
    const selectedProf = accountRisk.riskProfiles.find(p => p.id === selectedProfileId);
    if (!selectedProf) return;
    const newType = isFixed ? 'fixed' as const : 'percentage' as const;
    const updatedRisk: RiskSpec = { type: newType, value: defaultValueForType(newType) };
    handleUpdateProfile(selectedProfileId, { ...selectedProf, accountDefault: updatedRisk });
  }, [accountRisk, selectedProfileId, handleUpdateProfile]);

  // ---------------------------------------------------------------------------
  // Modal handlers
  // ---------------------------------------------------------------------------
  const handleOpenCreateModal = useCallback((): void => {
    setProfileModalMode('create');
    setEditingName('');
  }, []);

  const handleOpenRenameModal = useCallback((): void => {
    if (!selectedProfile) return;
    setProfileModalMode('rename');
    setEditingName(selectedProfile.name);
  }, [selectedProfile]);

  const handleCloseModal = useCallback((): void => {
    setProfileModalMode(null);
    setEditingName('');
  }, []);

  const handleModalSubmit = useCallback((): void => {
    const trimmed = editingName.trim();
    if (!trimmed) return;

    if (profileModalMode === 'create') {
      if (isNameDuplicate(trimmed)) return;
      handleAddProfile(trimmed);
    } else if (profileModalMode === 'rename' && selectedProfileId) {
      if (isNameDuplicate(trimmed, selectedProfileId)) return;
      handleRenameProfile(selectedProfileId, trimmed);
    }
    handleCloseModal();
  }, [editingName, profileModalMode, selectedProfileId, isNameDuplicate, handleAddProfile, handleRenameProfile, handleCloseModal]);

  const isModalNameInvalid = profileModalMode === 'create'
    ? isNameDuplicate(editingName)
    : profileModalMode === 'rename' && selectedProfileId
      ? isNameDuplicate(editingName, selectedProfileId)
      : false;

  const isModalSubmitDisabled = !editingName.trim() || isModalNameInvalid;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Parent guarantees accountRisk is non-null, but guard for type safety
  if (!accountRisk) return null;

  return (
    <div className="space-y-4">
      {/* ---- Profile Selector Row ---- */}
      <div className="flex items-center gap-2">
        <Select
          aria-label="Risk profile"
          selectedKeys={selectedProfileId ? [selectedProfileId] : []}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0];
            if (key) setSelectedProfileId(String(key));
          }}
          size="sm"
          className="flex-1"
        >
          {activeProfiles.map((profile) => (
            <SelectItem
              key={profile.id}
              textValue={profile.id === defaultProfileId ? `${profile.name} ★` : profile.name}
            >
              <div className="flex items-center gap-2">
                <span>{profile.name}</span>
                {profile.id === defaultProfileId && (
                  <Star className="w-3.5 h-3.5 text-warning shrink-0" fill="currentColor" />
                )}
              </div>
            </SelectItem>
          ))}
        </Select>

        <Button
          isIconOnly
          size="sm"
          variant="flat"
          color="primary"
          onPress={handleOpenCreateModal}
          aria-label="Add profile"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* ---- Profile Actions Row ---- */}
      {selectedProfile && selectedProfileId && (
        <div className="flex items-center gap-2">
          {/* Rename */}
          <Button
            size="sm"
            variant="flat"
            startContent={<Pencil className="w-3.5 h-3.5" />}
            onPress={handleOpenRenameModal}
          >
            Rename
          </Button>

          {/* Set Default / Default indicator */}
          {selectedProfileId === defaultProfileId ? (
            <div className="flex items-center gap-1 px-2 text-sm text-warning">
              <Star className="w-3.5 h-3.5" fill="currentColor" />
              <span>Default</span>
            </div>
          ) : (
            <Popover
              placement="bottom"
              isOpen={confirmingDefaultId === selectedProfileId}
              onOpenChange={(open) => {
                if (!open) setConfirmingDefaultId(null);
              }}
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="flat"
                  onPress={() => setConfirmingDefaultId(selectedProfileId)}
                  aria-label={`Set ${selectedProfile.name} as default`}
                >
                  <Star className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="flex flex-col gap-2 p-2">
                  <p className="text-sm">Set as default?</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      color="primary"
                      onPress={() => {
                        handleSetDefault(selectedProfileId);
                        setConfirmingDefaultId(null);
                      }}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      onPress={() => setConfirmingDefaultId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Archive / Delete */}
          {canDeleteProfile(selectedProfileId) && (
            <Button
              size="sm"
              variant="flat"
              color="danger"
              startContent={<Trash2 className="w-3.5 h-3.5" />}
              onPress={() => handleDeleteProfile(selectedProfileId)}
            >
              Archive
            </Button>
          )}
        </div>
      )}

      {/* ---- Profile Description ---- */}
      {selectedProfile && selectedProfileId && (
        <TextArea
          label="Description"
          placeholder="e.g., 'Clean setup, no negative confluences'"
          value={selectedProfile.description || ''}
          onValueChange={(value) => handleUpdateDescription(selectedProfileId, value)}
          minRows={1}
          maxRows={3}
          size="sm"
        />
      )}

      {/* ---- Account Default Risk ---- */}
      {selectedProfile && selectedProfileId && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
            Account Default Risk
          </p>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              step="any"
              label="Risk"
              labelPlacement="outside-left"
              value={toDisplayValue(selectedProfile.accountDefault)}
              onChange={(e) => handleAccountDefaultInputChange(e.target.value)}
              min="0"
              {...(selectedProfile.accountDefault.type === 'percentage' ? { max: '100' } : {})}
              classNames={{
                label: 'text-sm',
                input: 'text-right',
              }}
              size="sm"
            />
            <ButtonGroup
              options={[
                { value: 'percentage', label: '%' },
                { value: 'fixed', label: currency },
              ]}
              value={selectedProfile.accountDefault.type}
              onChange={(val) => handleAccountDefaultTypeToggle(val === 'fixed')}
              size="sm"
              variant="flat"
            />
          </div>
        </div>
      )}

      {/* ---- Cascade Editor ---- */}
      {selectedProfile && selectedProfileId && (
        <MobileCascadeEditor
          profile={selectedProfile}
          strategies={strategies}
          strategyPlaybooks={strategyPlaybooks}
          onUpdateProfile={(updated) => handleUpdateProfile(selectedProfileId, updated)}
          currency={currency}
        />
      )}

      {/* ---- Profile Name Modal (Create / Rename) ---- */}
      <Modal
        isOpen={profileModalMode !== null}
        onClose={handleCloseModal}
        size="sm"
      >
        <ModalContent>
          <ModalHeader>
            {profileModalMode === 'create' ? 'Add Profile' : 'Rename Profile'}
          </ModalHeader>
          <ModalBody>
            <Input
              label="Profile Name"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              isInvalid={isModalNameInvalid}
              errorMessage={isModalNameInvalid ? 'A profile with this name already exists' : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isModalSubmitDisabled) handleModalSubmit();
              }}
              autoFocus
              size="sm"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="flat"
              size="sm"
              onPress={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              isDisabled={isModalSubmitDisabled}
              onPress={handleModalSubmit}
            >
              {profileModalMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
